// Projects — the words, states and sums the Projects list and a Project page
// share. Pure, so the screens stay layout and the rules are tested here.

import { addDaysToKey } from './dates';

/** The app's five stages, indexed by projects.stage_idx (DESIGN-SYNC: the app's names stay). */
export const STAGES = ['Concept', 'Design', 'Production', 'Review', 'Delivery'] as const;
/**
 * Where a project is, 0–4. The stage's name wins when it is one of the five:
 * the seed and older rows carry a stage_idx counted on another list (Atlas is
 * "Design" at index 2), and the name is what someone chose.
 */
export const stageIndex = (p: { stage?: string | null; stage_idx: number }): number => {
  const named = STAGES.findIndex((s) => s.toLowerCase() === (p.stage ?? '').trim().toLowerCase());
  return named >= 0 ? named : Math.max(0, Math.min(STAGES.length - 1, p.stage_idx));
};
export const stageName = (idx: number): string =>
  STAGES[Math.max(0, Math.min(STAGES.length - 1, idx))];

// ── Details someone sets (20261104120000_project_details.sql) ───────────────
// The stored words and the ones people read. projects_details_check accepts
// exactly these when a value changes, so a new option goes there too.

/** projects.status. 'risk' and 'blocked' are older statuses still read (projectState). */
export const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'on-hold', label: 'On hold' },
  { value: 'done', label: 'Done' },
  { value: 'cancelled', label: 'Cancelled' },
] as const;
/** projects.health. Off track is stored as 'blocked', the word every reader already knows. */
export const HEALTH_OPTIONS = [
  { value: 'on-track', label: 'On track' },
  { value: 'at-risk', label: 'At risk' },
  { value: 'blocked', label: 'Off track' },
] as const;
/** projects.priority. */
export const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
] as const;

/**
 * A select's options with the row's current value kept: an older row holding a
 * word no longer offered shows it (as stored), rather than a blank that would
 * read as unset.
 */
export const withCurrent = (
  options: readonly { value: string; label: string }[],
  current: string | null,
): { value: string; label: string }[] =>
  !current || options.some((o) => o.value === current)
    ? [...options]
    : [...options, { value: current, label: current }];

/**
 * A deal value typed in, in rupees: digits with optional paise, commas and
 * spaces ignored. '' is "none" (null). Undefined: not a number we store.
 */
export const parseAmount = (text: string): number | null | undefined => {
  const t = text.replace(/[,\s₹]/g, '');
  if (!t) return null;
  if (!/^\d{1,12}(\.\d{1,2})?$/.test(t)) return undefined;
  return Number(t);
};

/**
 * One state per project, so the filter tabs' counts add up to All. Read from
 * both status and health: the schedule and the old Dashboard flagged a project
 * at risk by either, and a list that checked one would hide the other.
 */
export type ProjectState = 'active' | 'risk' | 'blocked' | 'shipped';
export const projectState = (p: { status: string; health?: string | null }): ProjectState =>
  p.status === 'done'
    ? 'shipped'
    : p.status === 'blocked' || p.health === 'blocked'
      ? 'blocked'
      : p.status === 'risk' || p.health === 'at-risk'
        ? 'risk'
        : 'active';

/** Friendly words, never the stored codes ('risk', 'done'). */
export const STATE_LABEL: Record<ProjectState, string> = {
  active: 'On track',
  risk: 'At risk',
  blocked: 'Blocked',
  shipped: 'Shipped',
};
/** Sage healthy, amber behind now, rose wrong; a shipped project steps back. */
export const STATE_TONE = {
  active: 'sage',
  risk: 'amber',
  blocked: 'rose',
  shipped: 'neutral',
} as const satisfies Record<ProjectState, string>;

/** Whole calendar days from `today` to `deadline` ('YYYY-MM-DD' keys, the org's calendar). */
export const daysUntil = (deadline: string, today: string): number =>
  Math.round((Date.parse(`${deadline}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000);

/** The countdown under a delivery date, and whether it is late (rose). Due soon is not late. */
export const countdown = (
  deadline: string | null,
  today: string,
  shipped: boolean,
): { text: string; late: boolean } | null => {
  if (shipped) return { text: 'Shipped', late: false };
  if (!deadline) return null;
  const d = daysUntil(deadline, today);
  if (d < 0) return { text: `${-d}d overdue`, late: true };
  if (d === 0) return { text: 'Due today', late: false };
  return { text: `in ${d}d`, late: false };
};

/** Not shipped, and due between today and two weeks out. */
export const dueWithin = (
  p: { deadline: string | null; status: string; health?: string | null },
  today: string,
  days = 14,
): boolean =>
  !!p.deadline &&
  projectState(p) !== 'shipped' &&
  p.deadline >= today &&
  p.deadline <= addDaysToKey(today, days);

export type SortKey = 'delivery' | 'progress' | 'client';
type Sortable = { deadline: string | null; progress: number; client: string | null; name: string };
const byName = (a: Sortable, b: Sortable) => a.name.localeCompare(b.name);
/** Delivery soonest first (undated last), progress highest first, client A–Z (internal last). */
export const sortProjects = <T extends Sortable>(rows: T[], key: SortKey): T[] =>
  [...rows].sort((a, b) => {
    if (key === 'progress') return b.progress - a.progress || byName(a, b);
    if (key === 'client') {
      if (!a.client !== !b.client) return a.client ? -1 : 1;
      return (a.client ?? '').localeCompare(b.client ?? '') || byName(a, b);
    }
    if (!a.deadline !== !b.deadline) return a.deadline ? -1 : 1;
    return (a.deadline ?? '').localeCompare(b.deadline ?? '') || byName(a, b);
  });

/** Search over name, code and client. */
export const matchesQuery = (
  p: { name: string; code: string | null; client: string | null },
  q: string,
): boolean => {
  const s = q.trim().toLowerCase();
  return !s || [p.name, p.code, p.client].some((v) => (v ?? '').toLowerCase().includes(s));
};

/**
 * create_project_from_deal's refusals in the words of the person who typed
 * the ID (20261014120000_crm_deals_money.sql). Anything else is shown as is.
 */
export const dealSetupError = (message: string): string => {
  if (/not found/i.test(message))
    return 'That deal isn’t here yet. Zoho deals sync every 15 minutes, so a deal won a moment ago may not have arrived. Check the ID and try again shortly.';
  if (/already has a project/i.test(message)) return 'That deal already has a project.';
  if (/projects=full/i.test(message)) return 'Setting up a project needs full Projects access.';
  return message;
};

// ── Keys and people (20261016090000_create_project.sql) ─────────────────────

/** A project key, as the database checks it: Jira's rule, a letter then letters, digits or _. */
export const KEY_RE = /^[A-Z][A-Z0-9_]{1,49}$/;
export const keyError = (key: string): string | undefined =>
  !key
    ? 'Give the project a key.'
    : KEY_RE.test(key)
      ? undefined
      : 'A key starts with a letter and has only letters, digits or _, at least 2 characters.';

/**
 * The key suggested from a name, mirroring suggest_project_key: the initials of
 * up to four words, never starting with a digit ("Marketing Campaign" gives
 * MC); under two letters, the first three of the name ("Atlas" gives ATL);
 * nothing usable gives PRJ. The server adds digits when it is taken.
 */
export const suggestKey = (name: string): string => {
  const words = name
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim()
    .split(' ');
  let k = words
    .slice(0, 4)
    .map((w) => w.slice(0, 1))
    .join('')
    .replace(/^[0-9]+/, '');
  if (k.length < 2)
    k = words
      .join('')
      .replace(/^[0-9]+/, '')
      .slice(0, 3);
  if (k.length < 2) k = `${k}PRJ`.slice(0, 3);
  return k;
};

/** project_members.project_role in Jira's words (spec §2); the enum stays. */
export type ProjectRole = 'lead' | 'contributor' | 'viewer';
export const ROLE_OPTIONS: { value: ProjectRole; label: string }[] = [
  { value: 'lead', label: 'Lead' },
  { value: 'contributor', label: 'Member' },
  { value: 'viewer', label: 'Viewer' },
];
export const roleName = (r: string): string => ROLE_OPTIONS.find((o) => o.value === r)?.label ?? r;

/** A Jira issue's page, when the site is configured (VITE_JIRA_SITE_URL). */
export const jiraIssueUrl = (site: string, key: string | null): string | null =>
  site && key ? `${site.replace(/\/+$/, '')}/browse/${encodeURIComponent(key)}` : null;
